import { compareSearchTextMatchSortMeta, type SearchTextMatchSortMeta } from '@kc-console/core';

const EMPTY_SECTION_META: SearchTextMatchSortMeta = {
  tier: Number.POSITIVE_INFINITY,
  matchIndex: Number.POSITIVE_INFINITY,
};

const bestSearchSortMeta = (metas: readonly SearchTextMatchSortMeta[]): SearchTextMatchSortMeta =>
  metas.reduce(
    (best, m) => (compareSearchTextMatchSortMeta(m, best) < 0 ? m : best),
    EMPTY_SECTION_META,
  );

/**
 * 섹션 행·리프 행 혼합 목록 정렬에 필요한 인자.
 */
export type SortSectionedRowsByLeafSearchMatchParams<TRow, TLeaf> = {
  /**
   * 섹션 내부 리프 목록을 정렬된 배열로 교체한 새 섹션 행을 만든다.
   */
  cloneSectionWithLeaves: (section: TRow, sortedLeaves: TLeaf[]) => TRow;
  /**
   * 같은 섹션 안에서 두 리프의 우선순위를 비교한다. `Array.prototype.sort`와 동일한 부호 규칙.
   */
  compareLeaves: (a: TLeaf, b: TLeaf, keyword: string) => number;
  /**
   * 리프 한 개에 대한 검색 정렬 메타. tier가 같으면 `matchIndex`(정규화 문자열 `indexOf`)가 작을수록 앞에 둔다.
   */
  getLeafSearchSortMeta: (leaf: TLeaf, keyword: string) => SearchTextMatchSortMeta;
  /**
   * 루트 정렬 시 리프 행끼리 동점일 때 사용하는 비교용 라벨.
   */
  getLeafSortLabel: (leaf: TLeaf) => string;
  /**
   * 섹션 행에서 정렬 대상인 리프 배열을 꺼낸다.
   */
  getSectionLeaves: (section: TRow) => readonly TLeaf[];
  /**
   * 루트 정렬 시 섹션 행끼리 동점일 때 사용하는 비교용 라벨 (예: 섹션 제목).
   */
  getSectionSortLabel: (section: TRow) => string;
  /**
   * 해당 행이 섹션인지 여부. `true`이면 `getSectionLeaves`·`cloneSectionWithLeaves` 등이 사용된다.
   */
  isSection: (row: TRow) => boolean;
  /**
   * 정렬할 원본 행 목록. 섹션 행과 리프 행이 섞여 있을 수 있다.
   */
  rows: readonly TRow[];
  /**
   * 사용자 입력 검색어. 내부에서 trim 후 비어 있으면 원본 `rows`의 얕은 복사를 그대로 반환한다.
   */
  searchKeyword: string;
  /**
   * 검색 메타 동점 시 루트 행 정렬에서 `tieLabel` `localeCompare` 대신 동점으로 둔다.
   * 안정 정렬이면 제공된 행 순서가 유지된다.
   */
  preserveStableOrderWhenSearchMetaTies?: boolean;
};

/**
 * `type === 'section'` 형태의 행과 리프 행이 섞인 목록을 검색 점수 기준으로 정렬한다.
 * @description
 * - 각 섹션의 리프는 먼저 정렬된 뒤, 루트에서는 섹션·리프를 베스트 검색 정렬 메타(tier·indexOf)로 섞는다.
 */
export function sortSectionedRowsByLeafSearchMatch<TRow, TLeaf>(
  params: SortSectionedRowsByLeafSearchMatchParams<TRow, TLeaf>,
): TRow[] {
  const kw = params.searchKeyword.trim();
  if (!kw) return [...params.rows];

  const keyword = params.searchKeyword;

  const withSortedSections = params.rows.map(row => {
    if (!params.isSection(row)) return row;
    const sortedLeaves = [...params.getSectionLeaves(row)].sort((a, b) =>
      params.compareLeaves(a, b, keyword),
    );
    return params.cloneSectionWithLeaves(row, sortedLeaves);
  });

  const sortKeyMeta = (row: TRow): SearchTextMatchSortMeta => {
    if (params.isSection(row)) {
      const leaves = params.getSectionLeaves(row);
      return bestSearchSortMeta(leaves.map(leaf => params.getLeafSearchSortMeta(leaf, keyword)));
    }
    return params.getLeafSearchSortMeta(row as unknown as TLeaf, keyword);
  };

  const tieLabel = (row: TRow): string =>
    params.isSection(row)
      ? params.getSectionSortLabel(row)
      : params.getLeafSortLabel(row as unknown as TLeaf);

  const preserveRootTieOrder = params.preserveStableOrderWhenSearchMetaTies === true;

  return [...withSortedSections].sort((a, b) => {
    const ma = sortKeyMeta(a);
    const mb = sortKeyMeta(b);
    const byMeta = compareSearchTextMatchSortMeta(ma, mb);
    if (byMeta !== 0) return byMeta;
    if (preserveRootTieOrder) return 0;
    return tieLabel(a).localeCompare(tieLabel(b), undefined, { sensitivity: 'base' });
  });
}
